"use client";

import {
  PAYWALL_GUIDED_ANCHOR_EXPERIMENT_KEY,
  PAYWALL_GUIDED_ANCHOR_VARIANTS,
  PaywallGuidedAnchorVariant,
  parsePaywallGuidedAnchorVariant,
} from "@/lib/experiments/paywallGuidedAnchor";
import { useEffect, useState } from "react";

type FeatureFlagLoadContext = {
  errorsLoading?: boolean;
};

export type PaywallExperimentPostHogClient = {
  get_distinct_id: () => string;
  getFeatureFlagResult: (
    key: string,
    options?: { fresh?: boolean; send_event?: boolean }
  ) => { variant?: string } | undefined;
  onFeatureFlags: (
    callback: (
      flags: string[],
      variants: Record<string, string | boolean>,
      context?: FeatureFlagLoadContext
    ) => void
  ) => () => void;
  on: (event: "featureFlagsReloading", callback: () => void) => () => void;
  reloadFeatureFlags: () => void;
};

export type PaywallGuidedAnchorExperimentResolution = {
  status: "loading" | "resolved";
  variant: PaywallGuidedAnchorVariant;
  source: "pending" | "posthog" | "fallback" | "bypassed";
  reason?: "disabled" | "invalid_variant" | "load_error" | "timeout" | "unavailable";
};

type PaywallGuidedAnchorExperimentState = {
  resolution: PaywallGuidedAnchorExperimentResolution;
  scopeKey: string;
};

const LOADING_RESOLUTION: PaywallGuidedAnchorExperimentResolution = {
  status: "loading",
  variant: PAYWALL_GUIDED_ANCHOR_VARIANTS.control,
  source: "pending",
};

function fallbackResolution(
  reason: NonNullable<PaywallGuidedAnchorExperimentResolution["reason"]>,
  source: "fallback" | "bypassed" = "fallback"
): PaywallGuidedAnchorExperimentResolution {
  return {
    status: "resolved",
    variant: PAYWALL_GUIDED_ANCHOR_VARIANTS.control,
    source,
    reason,
  };
}

export function usePaywallGuidedAnchorExperiment({
  allowAnonymous = false,
  enabled,
  posthog,
  timeoutMs = 1_500,
  userId,
}: {
  allowAnonymous?: boolean;
  enabled: boolean;
  posthog: PaywallExperimentPostHogClient | null | undefined;
  timeoutMs?: number;
  userId: string | null | undefined;
}): PaywallGuidedAnchorExperimentResolution {
  let subjectId = userId ?? null;
  if (!subjectId && allowAnonymous && posthog) {
    try {
      subjectId = posthog.get_distinct_id() || null;
    } catch {
      subjectId = null;
    }
  }

  const scopeKey = enabled
    ? `enabled:${subjectId ?? (allowAnonymous ? "unavailable" : "pending-user")}`
    : "disabled";
  const [state, setState] = useState<PaywallGuidedAnchorExperimentState>(() => ({
    resolution: enabled ? LOADING_RESOLUTION : fallbackResolution("disabled", "bypassed"),
    scopeKey,
  }));

  const visibleResolution =
    state.scopeKey === scopeKey
      ? state.resolution
      : enabled
        ? LOADING_RESOLUTION
        : fallbackResolution("disabled", "bypassed");

  useEffect(() => {
    const setScopedResolution = (resolution: PaywallGuidedAnchorExperimentResolution) => {
      setState({ resolution, scopeKey });
    };

    if (!enabled) {
      setScopedResolution(fallbackResolution("disabled", "bypassed"));
      return;
    }

    if (!subjectId) {
      setScopedResolution(allowAnonymous ? fallbackResolution("unavailable") : LOADING_RESOLUTION);
      return;
    }

    if (!posthog) {
      setScopedResolution(fallbackResolution("unavailable"));
      return;
    }

    let isSettled = false;
    let identifiedReloadStarted = false;
    let unsubscribeFromFlags: () => void = () => undefined;
    let unsubscribeFromReloading: () => void = () => undefined;

    const finish = (nextResolution: PaywallGuidedAnchorExperimentResolution) => {
      if (isSettled) return;
      isSettled = true;
      window.clearTimeout(timeoutId);
      setScopedResolution(nextResolution);
    };

    setScopedResolution(LOADING_RESOLUTION);

    const timeoutId = window.setTimeout(() => {
      finish(fallbackResolution("timeout"));
    }, timeoutMs);

    try {
      unsubscribeFromReloading = posthog.on("featureFlagsReloading", () => {
        if (isSettled) return;

        try {
          if (posthog.get_distinct_id() === subjectId) {
            identifiedReloadStarted = true;
          }
        } catch {
          finish(fallbackResolution("unavailable"));
        }
      });

      unsubscribeFromFlags = posthog.onFeatureFlags((_flags, _variants, context) => {
        if (isSettled) return;

        let distinctId: string;
        try {
          distinctId = posthog.get_distinct_id();
        } catch {
          finish(fallbackResolution("unavailable"));
          return;
        }

        // Standard paywall assignment is scoped to the authenticated Supabase
        // UUID. Onboarding can deliberately scope the same flag to PostHog's
        // current anonymous ID before signup. Never accept a response for an
        // identity other than the one that started this resolution.
        if (distinctId !== subjectId) return;

        // onFeatureFlags can immediately replay an older response when the
        // listener is registered. Only trust a response after an identified
        // reload has started for this page view.
        if (!identifiedReloadStarted) return;

        if (context?.errorsLoading) {
          finish(fallbackResolution("load_error"));
          return;
        }

        let flagResult: { variant?: string } | undefined;
        try {
          flagResult = posthog.getFeatureFlagResult(PAYWALL_GUIDED_ANCHOR_EXPERIMENT_KEY, {
            fresh: true,
            send_event: false,
          });
        } catch {
          finish(fallbackResolution("unavailable"));
          return;
        }

        // This callback is accepted only after an identified reload started, so
        // a missing fresh result now means the flag is inactive or absent. Fail
        // immediately instead of holding every control visitor on the skeleton.
        if (!flagResult) {
          finish(fallbackResolution("invalid_variant"));
          return;
        }

        const variant = parsePaywallGuidedAnchorVariant(flagResult.variant);
        if (!variant) {
          // A missing flag, boolean flag, or misspelled variant is not a valid
          // experiment assignment and must not masquerade as a control exposure.
          finish(fallbackResolution("invalid_variant"));
          return;
        }

        finish({
          status: "resolved",
          variant,
          source: "posthog",
        });
      });

      if (posthog.get_distinct_id() === subjectId) {
        posthog.reloadFeatureFlags();
      }
    } catch {
      finish(fallbackResolution("unavailable"));
    }

    return () => {
      isSettled = true;
      window.clearTimeout(timeoutId);
      unsubscribeFromFlags();
      unsubscribeFromReloading();
    };
  }, [allowAnonymous, enabled, posthog, scopeKey, subjectId, timeoutMs]);

  return visibleResolution;
}

export function exposePaywallGuidedAnchorExperiment(
  posthog: PaywallExperimentPostHogClient,
  renderedVariant: PaywallGuidedAnchorVariant
) {
  try {
    const currentResult = posthog.getFeatureFlagResult(PAYWALL_GUIDED_ANCHOR_EXPERIMENT_KEY, {
      fresh: true,
      send_event: false,
    });
    const currentVariant = parsePaywallGuidedAnchorVariant(currentResult?.variant);
    if (currentVariant !== renderedVariant) return false;

    // This second lookup is the one standard PostHog exposure. It runs from a
    // React effect after the assigned paywall has committed to the DOM.
    posthog.getFeatureFlagResult(PAYWALL_GUIDED_ANCHOR_EXPERIMENT_KEY, {
      fresh: true,
      send_event: true,
    });
    return true;
  } catch {
    return false;
  }
}
